import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home as HomeIcon, ChevronRight } from 'lucide-react';

/**
 * Maps a URL slug to a human-readable breadcrumb label.
 * Add new slugs here when new routes are introduced.
 */
const LABELS = {
  '':              'Home',
  'platform':      'Platform',
  'sectors':       'Sectors',
  'nhs':           'NHS',
  'hard-services': 'FM Hard Services',
  'construction':  'Construction',
  'about':         'About',
  'contact':       'Contact',
  'consultancy':   'Consultancy',
  'resources':     'Resources',
};

const labelFor = (slug) => LABELS[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const Breadcrumbs = () => {
  const { pathname } = useLocation();
  const parts = pathname.split('/').filter(Boolean);

  // Don't render on the home page
  if (parts.length === 0) return null;

  const crumbs = parts.map((slug, i) => ({
    slug,
    label: labelFor(slug),
    href: '/' + parts.slice(0, i + 1).join('/'),
    last: i === parts.length - 1,
  }));

  return (
    <nav
      aria-label="Breadcrumb"
      className="bg-white border-b border-slate-100"
      data-testid="breadcrumbs"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-slate-500">
          <li>
            <Link
              to="/"
              data-testid="breadcrumb-home"
              className="inline-flex items-center gap-1 hover:text-slate-900 transition-colors"
            >
              <HomeIcon className="w-3.5 h-3.5" />
              <span className="sr-only sm:not-sr-only">Home</span>
            </Link>
          </li>
          {crumbs.map((c) => (
            <li key={c.href} className="inline-flex items-center gap-1.5">
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
              {c.last ? (
                <span
                  className="font-medium text-slate-900 truncate max-w-[12rem] sm:max-w-none"
                  aria-current="page"
                  data-testid={`breadcrumb-${c.slug}`}
                >
                  {c.label}
                </span>
              ) : (
                <Link
                  to={c.href}
                  data-testid={`breadcrumb-${c.slug}`}
                  className="hover:text-slate-900 transition-colors truncate max-w-[12rem] sm:max-w-none"
                >
                  {c.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
